import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Snackbar,
  Stack,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { AutoAwesome, ContentCopy, Lock, Upgrade } from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useChart } from '../contexts/ChartContext';
import {
  AstroLogoPayload,
  AstroLogoUsage,
  generateAstroLogo,
  getAstroLogoUsage
} from '../utils/astroLogoApi';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const BUSINESS_CATEGORIES = [
  'Astrology',
  'Numerology',
  'Spiritual Consultancy',
  'Education',
  'Finance',
  'Beauty',
  'Fashion',
  'Real Estate',
  'Health & Wellness',
  'Technology',
  'Personal Brand',
  'Other'
];

const STYLES = [
  'Modern Premium',
  'Traditional Vedic',
  'Luxury',
  'Spiritual Minimal',
  'Bold Corporate',
  'Creative Artistic'
];

const HINDI_OPTION_LABELS: Record<string, string> = {
  Astrology: 'ज्योतिष',
  Numerology: 'अंक ज्योतिष',
  'Spiritual Consultancy': 'आध्यात्मिक परामर्श',
  Education: 'शिक्षा',
  Finance: 'वित्त',
  Beauty: 'सौंदर्य',
  Fashion: 'फैशन',
  'Real Estate': 'रियल एस्टेट',
  'Health & Wellness': 'स्वास्थ्य और कल्याण',
  Technology: 'प्रौद्योगिकी',
  'Personal Brand': 'व्यक्तिगत ब्रांड',
  Other: 'अन्य',
  'Modern Premium': 'आधुनिक प्रीमियम',
  'Traditional Vedic': 'पारंपरिक वैदिक',
  Luxury: 'लक्जरी',
  'Spiritual Minimal': 'आध्यात्मिक न्यूनतम',
  'Bold Corporate': 'मजबूत कॉर्पोरेट',
  'Creative Artistic': 'रचनात्मक कलात्मक',
  Sun: 'सूर्य',
  Moon: 'चंद्र',
  Mars: 'मंगल',
  Mercury: 'बुध',
  Jupiter: 'गुरु',
  Venus: 'शुक्र',
  Saturn: 'शनि',
  Rahu: 'राहु',
  Ketu: 'केतु',
  Aries: 'मेष',
  Taurus: 'वृषभ',
  Gemini: 'मिथुन',
  Cancer: 'कर्क',
  Leo: 'सिंह',
  Virgo: 'कन्या',
  Libra: 'तुला',
  Scorpio: 'वृश्चिक',
  Sagittarius: 'धनु',
  Capricorn: 'मकर',
  Aquarius: 'कुंभ',
  Pisces: 'मीन',
  Sunday: 'रविवार',
  Monday: 'सोमवार',
  Tuesday: 'मंगलवार',
  Wednesday: 'बुधवार',
  Thursday: 'गुरुवार',
  Friday: 'शुक्रवार',
  Saturday: 'शनिवार',
  Ashwini: 'अश्विनी',
  Bharani: 'भरणी',
  Krittika: 'कृत्तिका',
  Rohini: 'रोहिणी',
  Mrigashira: 'मृगशिरा',
  Ardra: 'आर्द्रा',
  Punarvasu: 'पुनर्वसु',
  Pushya: 'पुष्य',
  Ashlesha: 'आश्लेषा',
  Magha: 'मघा',
  'Purva Phalguni': 'पूर्व फाल्गुनी',
  'Uttara Phalguni': 'उत्तर फाल्गुनी',
  Hasta: 'हस्त',
  Chitra: 'चित्रा',
  Swati: 'स्वाती',
  Vishakha: 'विशाखा',
  Anuradha: 'अनुराधा',
  Jyeshtha: 'ज्येष्ठा',
  Mula: 'मूल',
  'Purva Ashadha': 'पूर्वाषाढ़ा',
  'Uttara Ashadha': 'उत्तराषाढ़ा',
  Shravana: 'श्रवण',
  Dhanishta: 'धनिष्ठा',
  Shatabhisha: 'शतभिषा',
  'Purva Bhadrapada': 'पूर्व भाद्रपद',
  'Uttara Bhadrapada': 'उत्तर भाद्रपद',
  Revati: 'रेवती',
  Pratipada: 'प्रतिपदा',
  Dwitiya: 'द्वितीया',
  Tritiya: 'तृतीया',
  Chaturthi: 'चतुर्थी',
  Panchami: 'पंचमी',
  Shashthi: 'षष्ठी',
  Saptami: 'सप्तमी',
  Ashtami: 'अष्टमी',
  Navami: 'नवमी',
  Dashami: 'दशमी',
  Ekadashi: 'एकादशी',
  Dwadashi: 'द्वादशी',
  Trayodashi: 'त्रयोदशी',
  Chaturdashi: 'चतुर्दशी',
  Purnima: 'पूर्णिमा',
  Amavasya: 'अमावस्या',
  Bava: 'बव',
  Balava: 'बालव',
  Kaulava: 'कौलव',
  Taitila: 'तैतिल',
  Gara: 'गर',
  Vanija: 'वणिज',
  Vishti: 'विष्टि',
  Shakuni: 'शकुनि',
  Chatushpada: 'चतुष्पद',
  Naga: 'नाग',
  Kimstughna: 'किंस्तुघ्न',
  Shubha: 'शुभ',
  Preeti: 'प्रीति',
  Siddhi: 'सिद्धि',
};

const TITHI_BASE_NAMES = [
  'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami', 'Shashthi', 'Saptami', 'Ashtami',
  'Navami', 'Dashami', 'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima', 'Amavasya'
];

const VARA_MAP: Record<string, string> = {
  Ravivara: 'Sunday',
  Somavara: 'Monday',
  Mangalavara: 'Tuesday',
  Budhavara: 'Wednesday',
  Guruvara: 'Thursday',
  Shukravara: 'Friday',
  Shanivara: 'Saturday'
};

const normalizePlanet = (value?: string) => {
  if (!value) return '';
  const normalized = value.toLowerCase();
  if (normalized === 'sun') return 'Sun';
  if (normalized === 'moon') return 'Moon';
  if (normalized === 'mars') return 'Mars';
  if (normalized === 'mercury') return 'Mercury';
  if (normalized === 'jupiter') return 'Jupiter';
  if (normalized === 'venus') return 'Venus';
  if (normalized === 'saturn') return 'Saturn';
  if (normalized === 'rahu') return 'Rahu';
  if (normalized === 'ketu') return 'Ketu';
  return value;
};

const normalizeTithi = (value?: string) => {
  if (!value) return '';
  return TITHI_BASE_NAMES.find((name) => value.includes(name)) || value;
};

const normalizeVara = (value?: string) => {
  if (!value) return '';
  return VARA_MAP[value] || value;
};

const normalizeKarana = (value?: string) => {
  if (!value) return '';
  if (value === 'Garija') return 'Gara';
  return value;
};

const normalizeYoga = (value?: string) => {
  if (!value) return '';
  if (value === 'Priti') return 'Preeti';
  return value;
};

const toDateOnly = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const deriveLogoPayloadFromChart = (chart: any): Partial<AstroLogoPayload> => {
  const moon = chart?.planets?.MOON || chart?.planets?.Moon || chart?.planets?.moon;
  const ascendant = chart?.houses?.ascendant;

  return {
    brandName: chart?.birthDetails?.name || '',
    lagna: ascendant?.sign?.name || '',
    moonRashi: moon?.sign?.name || '',
    moonNakshatra: moon?.nakshatra?.name || '',
    nakshatraPada: moon?.nakshatra?.pada ? String(moon.nakshatra.pada) : '',
    dominantPlanet: normalizePlanet(moon?.nakshatra?.lord || ascendant?.sign?.ruler || '')
  };
};

const AstroLogoDesigner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const { state } = useAuth();
  const { currentChart, setCurrentChart } = useChart();
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');
  const text = useCallback((en: string, hi: string) => isHindi ? hi : en, [isHindi]);
  const optionLabel = useCallback((value?: string) => {
    if (!value) return '';
    return isHindi ? (HINDI_OPTION_LABELS[value] || value) : value;
  }, [isHindi]);
  const [chart, setChart] = useState<any>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [panchangLoading, setPanchangLoading] = useState(false);
  const [usage, setUsage] = useState<AstroLogoUsage | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState<AstroLogoPayload>({
    brandName: '',
    businessCategory: '',
    preferredStyle: 'Modern Premium',
    lagna: '',
    moonRashi: '',
    moonNakshatra: '',
    nakshatraPada: '',
    tithi: '',
    vara: '',
    yoga: '',
    karana: '',
    dominantPlanet: ''
  });

  const isLoggedIn = state.isAuthenticated && Boolean(state.user);
  const hasRequiredChartData = Boolean(form.moonNakshatra && form.dominantPlanet);
  const canGenerate = Boolean(
    isLoggedIn &&
    !chartLoading &&
    !panchangLoading &&
    form.brandName.trim() &&
    form.businessCategory &&
    hasRequiredChartData &&
    (usage?.isPremium || Number(usage?.remaining || 0) > 0)
  );

  const usageLabel = useMemo(() => {
    if (!isLoggedIn) return text('Login required', 'लॉगिन आवश्यक है');
    if (!usage) return text('Checking usage...', 'उपयोग सीमा जांची जा रही है...');
    if (usage.isPremium) {
      return text(
        'Premium Active: Unlimited logo generations available.',
        'प्रीमियम सक्रिय: असीमित लोगो निर्माण उपलब्ध है।'
      );
    }
    return text(
      `Free Trial: ${usage.remaining} of ${usage.freeLimit} logo generations remaining.`,
      `निःशुल्क परीक्षण: ${usage.freeLimit} में से ${usage.remaining} लोगो निर्माण शेष हैं।`
    );
  }, [isLoggedIn, text, usage]);

  useEffect(() => {
    const loadChart = async () => {
      if (!id) return;
      if (id === 'preview' && currentChart) {
        setChart(currentChart);
        return;
      }
      if (currentChart && (currentChart._id === id || currentChart.chartId === id)) {
        setChart(currentChart);
        return;
      }

      setChartLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API_BASE_URL}/charts/${id}`);
        const loadedChart = response.data.data.chart;
        setChart(loadedChart);
        setCurrentChart(loadedChart);
      } catch (err: any) {
        setError(err.response?.data?.message || text('Failed to load Kundli for Astro Logo Designer.', 'एस्ट्रो ब्रांड लोगो डिजाइनर के लिए कुंडली लोड नहीं हो सकी।'));
      } finally {
        setChartLoading(false);
      }
    };

    loadChart();
  }, [id, currentChart, setCurrentChart, text]);

  useEffect(() => {
    if (!chart) return;
    setResult(null);
    setForm((current) => ({
      ...current,
      ...deriveLogoPayloadFromChart(chart),
      preferredStyle: current.preferredStyle || 'Modern Premium',
      businessCategory: current.businessCategory
    }));
  }, [chart]);

  useEffect(() => {
    const loadBirthPanchang = async () => {
      if (!chart?.birthDetails?.dateOfBirth || !chart?.birthDetails?.placeOfBirth) return;

      const place = chart.birthDetails.placeOfBirth;
      const date = toDateOnly(chart.birthDetails.dateOfBirth);
      setPanchangLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/panchang`, {
          params: {
            date,
            lat: place.latitude,
            lon: place.longitude,
            timezoneOffset: place.timezone ?? 330,
            ayanamshaMode: chart.calculationData?.ayanamshaMode || 'LAHIRI'
          }
        });
        const panchang = response.data.data.panchang;
        setForm((current) => ({
          ...current,
          tithi: normalizeTithi(panchang?.tithi),
          vara: normalizeVara(panchang?.day),
          yoga: normalizeYoga(panchang?.yoga),
          karana: normalizeKarana(panchang?.karana),
          moonRashi: current.moonRashi || panchang?.rashi?.moonSign || '',
          moonNakshatra: current.moonNakshatra || panchang?.nakshatra || '',
          nakshatraPada: current.nakshatraPada || (panchang?.details?.nakshatraPada ? String(panchang.details.nakshatraPada) : '')
        }));
      } catch (err) {
        console.warn('Could not load birth Panchang for Astro Logo Designer:', err);
      } finally {
        setPanchangLoading(false);
      }
    };

    loadBirthPanchang();
  }, [chart]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const loadUsage = async () => {
      setLoadingUsage(true);
      try {
        const res = await getAstroLogoUsage();
        setUsage(res.data);
      } catch (err: any) {
        setError(err.response?.data?.message || text('Unable to fetch logo usage.', 'लोगो उपयोग सीमा नहीं मिल सकी।'));
      } finally {
        setLoadingUsage(false);
      }
    };

    loadUsage();
  }, [isLoggedIn, text]);

  const updateField = (field: keyof AstroLogoPayload, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);

    try {
      const res = await generateAstroLogo({
        ...form,
        brandName: form.brandName.trim(),
        businessCategory: form.businessCategory.trim(),
        preferredStyle: form.preferredStyle || 'Modern Premium',
        language: isHindi ? 'hi' : 'en'
      });

      setResult(res.data);
      setUsage((current) => ({
        isPremium: res.access.isPremium,
        freeLimit: res.access.freeLimit,
        used: current?.isPremium ? (current?.used || 0) : Math.min((current?.used || 0) + 1, res.access.freeLimit),
        remaining: res.access.remainingAfterGeneration,
        totalGenerations: (current?.totalGenerations || 0) + 1,
        lastGeneratedAt: new Date().toISOString()
      }));
    } catch (err: any) {
      const apiError = err?.response?.data;
      if (apiError?.code === 'FREE_LIMIT_EXHAUSTED') {
        setError(apiError.message);
        setUsage((current) => current ? { ...current, remaining: 0 } : current);
      } else if (apiError?.code === 'LOGIN_REQUIRED') {
        setError(text('Please login to generate Kundli-based logo.', 'कुंडली-आधारित लोगो बनाने के लिए लॉगिन करें।'));
      } else {
        setError(apiError?.message || text('Unable to generate logo brief.', 'लोगो संक्षेप नहीं बन सका।'));
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Helmet>
        <title>{text('Astro Brand Logo Designer | Kundlistar', 'एस्ट्रो ब्रांड लोगो डिजाइनर | Kundlistar')}</title>
        <meta
          name="description"
          content={text(
            'Create personalized logo concepts using a loaded Kundli, Nakshatra, Panchang, Lagna and planetary energy.',
            'लोड की गई कुंडली, नक्षत्र, पंचांग, लग्न और ग्रह ऊर्जा से व्यक्तिगत लोगो संकल्पना बनाएं।'
          )}
        />
      </Helmet>

      <Box sx={{ mb: 4 }}>
        <Chip icon={<AutoAwesome />} label={text('Kundli-based plan feature', 'कुंडली-आधारित प्रीमियम सुविधा')} color="secondary" sx={{ mb: 2 }} />
        <Typography variant="h3" sx={{ fontWeight: 600, mb: 1 }}>
          {text('Astro Brand Logo Designer', 'एस्ट्रो ब्रांड लोगो डिजाइनर')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 860 }}>
          {text(
            'Create personalized logo concepts using the loaded Kundli, Moon Nakshatra, Panchang, Lagna and planetary energy. Free users get 2 logo generations. plan users get unlimited logo generations.',
            'लोड की गई कुंडली, चंद्र नक्षत्र, पंचांग, लग्न और ग्रह ऊर्जा से व्यक्तिगत लोगो संकल्पना बनाएं। निःशुल्क उपयोगकर्ताओं को 2 लोगो निर्माण मिलते हैं। प्रीमियम उपयोगकर्ताओं के लिए असीमित लोगो निर्माण उपलब्ध है।'
          )}
        </Typography>
      </Box>

      {(chartLoading || panchangLoading) && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 3 }}>
          {chartLoading
            ? text('Loading Kundli details...', 'कुंडली विवरण लोड हो रहे हैं...')
            : text('Calculating birth Panchang details...', 'जन्म पंचांग विवरण की गणना हो रही है...')}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {text('Access', 'प्रवेश')}
              </Typography>
              <Alert severity={usage?.isPremium ? 'success' : isLoggedIn ? 'info' : 'warning'} icon={!isLoggedIn ? <Lock /> : undefined}>
                {usageLabel}
              </Alert>
              {!isLoggedIn && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" component={RouterLink} to="/login">
                    {text('Login', 'लॉगिन')}
                  </Button>
                  <Button variant="outlined" component={RouterLink} to="/register">
                    {text('Register', 'रजिस्टर')}
                  </Button>
                </Stack>
              )}
              {isLoggedIn && !usage?.isPremium && usage?.remaining === 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {text(
                      'You have used your 2 free logo generations. Upgrade to Kundlistar Premium to generate unlimited Astro Logo concepts.',
                      'आपने अपने 2 निःशुल्क लोगो निर्माण उपयोग कर लिए हैं। असीमित एस्ट्रो लोगो संकल्पनाओं के लिए Kundlistar प्रीमियम में अपग्रेड करें।'
                    )}
                  </Typography>
                  <Button startIcon={<Upgrade />} variant="contained" component={RouterLink} to="/upgrade">
                    {text('Choose Plan', 'प्रीमियम में अपग्रेड करें')}
                  </Button>
                </Box>
              )}
              {chart && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{text('Loaded Kundli', 'लोड की गई कुंडली')}</Typography>
                  <Typography variant="body2">{chart.birthDetails?.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {toDateOnly(chart.birthDetails?.dateOfBirth)} · {chart.birthDetails?.placeOfBirth?.city || chart.birthDetails?.placeOfBirth?.country}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {text('Logo Inputs', 'लोगो इनपुट')}
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                {text(
                  'Kundli and Panchang fields are filled from the loaded chart. Only brand name, category and style are editable.',
                  'कुंडली और पंचांग के क्षेत्र लोड की गई कुंडली से अपने-आप भरे गए हैं। केवल ब्रांड नाम, श्रेणी और शैली बदली जा सकती है।'
                )}
              </Alert>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Brand / Person Name', 'ब्रांड / व्यक्ति का नाम')} value={form.brandName} onChange={(e) => updateField('brandName', e.target.value)} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField select fullWidth label={text('Business Category', 'व्यवसाय श्रेणी')} value={form.businessCategory} onChange={(e) => updateField('businessCategory', e.target.value)} SelectProps={{ renderValue: (value) => optionLabel(String(value)) }}>
                    {BUSINESS_CATEGORIES.map((item) => <MenuItem key={item} value={item}>{optionLabel(item)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField select fullWidth label={text('Preferred Style', 'पसंदीदा शैली')} value={form.preferredStyle} onChange={(e) => updateField('preferredStyle', e.target.value)} SelectProps={{ renderValue: (value) => optionLabel(String(value)) }}>
                    {STYLES.map((item) => <MenuItem key={item} value={item}>{optionLabel(item)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Moon Nakshatra', 'चंद्र नक्षत्र')} value={optionLabel(form.moonNakshatra)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Dominant Planet', 'प्रमुख ग्रह')} value={optionLabel(form.dominantPlanet)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Lagna', 'लग्न')} value={optionLabel(form.lagna)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Moon Rashi', 'चंद्र राशि')} value={optionLabel(form.moonRashi)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Nakshatra Pada', 'नक्षत्र पाद')} value={form.nakshatraPada} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Tithi', 'तिथि')} value={optionLabel(form.tithi)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Vara', 'वार')} value={optionLabel(form.vara)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Yoga', 'योग')} value={optionLabel(form.yoga)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label={text('Karana', 'करण')} value={optionLabel(form.karana)} InputProps={{ readOnly: true }} />
                </Grid>
              </Grid>

              {!hasRequiredChartData && !chartLoading && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {text(
                    'Moon Nakshatra or dominant planet is missing from this Kundli. Please reload or recalculate the chart.',
                    'इस कुंडली में चंद्र नक्षत्र या प्रमुख ग्रह नहीं मिला। कृपया कुंडली फिर से लोड करें या दोबारा गणना करें।'
                  )}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                sx={{ mt: 3 }}
                disabled={!canGenerate || generating || loadingUsage}
                onClick={handleGenerate}
              >
                {generating ? text('Generating...', 'बनाया जा रहा है...') : text('Generate Kundli-Based Logo', 'कुंडली-आधारित लोगो बनाएं')}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {result && <AstroLogoResult result={result} />}
    </Container>
  );
};

const AstroLogoResult: React.FC<{ result: any }> = ({ result }) => {
  const { i18n } = useTranslation();
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');
  const text = (en: string, hi: string) => isHindi ? hi : en;
  const [copiedPromptIndex, setCopiedPromptIndex] = useState<number | null>(null);
  const [copyError, setCopyError] = useState('');

  const copyPrompt = async (prompt: string, index: number) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(prompt);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = prompt;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopyError('');
      setCopiedPromptIndex(index);
    } catch (error) {
      setCopiedPromptIndex(null);
      setCopyError(text('Unable to copy prompt. Please select and copy it manually.', 'निर्देश प्रतिलिपि नहीं हो सके। कृपया चुनकर स्वयं प्रतिलिपि करें।'));
    }
  };
  const kundliInputLabel = (key: string) => {
    const labels: Record<string, string> = {
      lagna: 'लग्न',
      moonRashi: 'चंद्र राशि',
      moonNakshatra: 'चंद्र नक्षत्र',
      nakshatraPada: 'नक्षत्र पाद',
      tithi: 'तिथि',
      vara: 'वार',
      yoga: 'योग',
      karana: 'करण',
      dominantPlanet: 'प्रमुख ग्रह'
    };

    return isHindi ? (labels[key] || key) : key;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
        {result.brandName} {text('Logo Design Brief', 'लोगो डिजाइन संक्षेप')}
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1">{result.summary}</Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1}>
            {Object.entries(result.kundliInputs || {}).map(([key, value]) => (
              value ? (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                  <Chip label={`${kundliInputLabel(key)}: ${value}`} variant="outlined" sx={{ maxWidth: '100%' }} />
                </Grid>
              ) : null
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {result.concepts.map((concept: any, index: number) => (
          <Grid size={{ xs: 12, md: 6 }} key={concept.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {text('Concept', 'संकल्पना')} {index + 1}: {concept.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>{concept.visualIdea}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">{text('Main Symbol', 'मुख्य प्रतीक')}</Typography>
                <Typography sx={{ mb: 1.5 }}>{concept.mainSymbol}</Typography>
                <ChipList title={text('Recommended Symbols', 'सुझाए गए प्रतीक')} items={concept.recommendedSymbols} />
                <ChipList title={text('Colors', 'रंग')} items={concept.colors} />
                <ChipList title={text('Shapes', 'आकार')} items={concept.shapes} />
                <Typography variant="subtitle2" sx={{ mt: 1.5 }}>{text('Typography', 'अक्षर शैली')}</Typography>
                <Typography>{concept.typography}</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={7}
                  value={concept.prompt}
                  sx={{ mt: 2 }}
                  InputProps={{ readOnly: true }}
                  label={text('Logo AI Prompt', 'लोगो निर्देश')}
                />
                <Button
                  startIcon={<ContentCopy />}
                  sx={{ mt: 1.5 }}
                  variant={copiedPromptIndex === index ? 'contained' : 'outlined'}
                  onClick={() => copyPrompt(concept.prompt, index)}
                >
                  {copiedPromptIndex === index ? text('Copied', 'प्रतिलिपि हो गई') : text('Copy Prompt', 'निर्देश प्रतिलिपि करें')}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <GuidanceCard title={text('Do', 'करें')} items={result.doAndDont.do} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <GuidanceCard title={text("Don't", 'न करें')} items={result.doAndDont.dont} />
        </Grid>
      </Grid>

      <Snackbar
        open={copiedPromptIndex !== null || Boolean(copyError)}
        autoHideDuration={2600}
        onClose={() => {
          setCopiedPromptIndex(null);
          setCopyError('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={copyError ? 'error' : 'success'}
          variant="filled"
          onClose={() => {
            setCopiedPromptIndex(null);
            setCopyError('');
          }}
        >
          {copyError || text('Logo AI prompt copied to clipboard.', 'लोगो निर्देश क्लिपबोर्ड में प्रतिलिपि हो गया।')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ChipList: React.FC<{ title: string; items: string[] }> = ({ title, items }) => {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{title}</Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {items.map((item) => <Chip key={item} label={item} size="small" />)}
      </Stack>
    </Box>
  );
};

const GuidanceCard: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <Card sx={{ mt: 3 }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Stack spacing={1}>
        {items.map((item) => <Typography key={item}>• {item}</Typography>)}
      </Stack>
    </CardContent>
  </Card>
);

export default AstroLogoDesigner;
